import { useQuery, useSuspenseQuery } from "@tanstack/react-query"
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router"

import { PageHeader } from "@/components/common/page-header"
import { RecordColumn } from "@/components/common/record"
import { RecordFields } from "@/components/common/record-fields"
import { RecordListField } from "@/components/common/record-list-field"
import { Button } from "@/components/ui/button"
import { Spinner } from "@/components/ui/spinner"
import { NationalInsuranceNumber } from "@/features/individuals/components/national-insurance-number"
import { individualTitleOptions } from "@/features/individuals/individual-titles"
import { residencyStatusOptions } from "@/features/individuals/residency-statuses"
import { useRecordForm } from "@/hooks/use-record-form"
import {
  countryOptions,
  countryQueries,
  nationalityOptions,
} from "@/queries/countries"
import { individualQueries, useUpdateIndividual } from "@/queries/individuals"
import type { Individual } from "@/types/individual"

const formId = "individual-record"

export const Route = createFileRoute(
  "/$organisation/_authenticated/_organisation/individuals/$individualId/record/edit/"
)({
  staticData: { attention: "individual.details" },
  loader: ({ context }) =>
    context.queryClient.ensureQueryData(countryQueries.list()),
  component: EditIndividualRecordPage,
})

function valuesFrom(individual: Individual) {
  return {
    title: individual.title ?? "",
    firstName: individual.firstName,
    middleNames: individual.middleNames ?? "",
    lastName: individual.lastName,
    email: individual.email ?? "",
    phone: individual.phone ?? "",
    dateOfBirth: individual.dateOfBirth ?? "",
    nationality: individual.nationality ?? "",
    secondaryNationality: individual.secondaryNationality ?? "",
    placeOfBirth: individual.placeOfBirth ?? "",
    birthCountry: individual.birthCountry ?? "",
    residencyStatus: individual.residencyStatus ?? "",
    previousNames:
      individual.previousNames.length > 0
        ? [...individual.previousNames]
        : [""],
  }
}

/**
 * Who somebody is as one plain column with Save in the header; back to the
 * view once saved. The NI number has its own save below: it never travels
 * with the record.
 */
function EditIndividualRecordPage() {
  const { individualId } = Route.useParams()
  const navigate = useNavigate()
  const { data: individual } = useSuspenseQuery(
    individualQueries.detail(individualId)
  )
  const { data: countries } = useQuery(countryQueries.list())

  const form = useRecordForm({
    initial: valuesFrom(individual),
    mutation: useUpdateIndividual(individualId),
    toValues: valuesFrom,
    toInput: (values) => ({
      ...values,
      previousNames: values.previousNames
        .map((name) => name.trim())
        .filter(Boolean),
    }),
    validate: (values) =>
      values.firstName.trim() === "" || values.lastName.trim() === ""
        ? { lastName: "A person needs a first and a last name." }
        : {},
  })

  const nationalities = nationalityOptions(countries?.items)

  return (
    <>
      <PageHeader
        title="Edit personal details"
        actions={
          <>
            <Button
              variant="ghost"
              disabled={form.saving}
              nativeButton={false}
              render={
                <Link
                  from="/$organisation"
                  to="/$organisation/individuals/$individualId/record"
                  params={{ individualId }}
                >
                  Cancel
                </Link>
              }
            />
            <Button
              type="submit"
              form={formId}
              disabled={!form.dirty || form.saving}
            >
              {form.saving ? <Spinner /> : null}
              Save
            </Button>
          </>
        }
      />

      <RecordColumn>
        <form
          id={formId}
          onSubmit={(event) =>
            void form.submit(event).then((saved) => {
              if (saved) {
                void navigate({
                  from: "/$organisation",
                  to: "/$organisation/individuals/$individualId/record",
                  params: { individualId },
                })
              }
            })
          }
          noValidate
          className="flex flex-col gap-5"
        >
          <RecordFields
            form={form}
            fields={[
              {
                name: "title",
                label: "Title",
                options: individualTitleOptions(individual.title),
              },
              { name: "firstName", label: "First name" },
              { name: "middleNames", label: "Middle names" },
              { name: "lastName", label: "Last name" },
            ]}
          />
          <RecordListField
            id="previous-names"
            label="Previous names"
            values={form.values.previousNames}
            onChange={(names) => form.setValue("previousNames", names)}
            errors={form.errorsFor("previousNames")}
          />
          <RecordFields
            form={form}
            fields={[
              { name: "email", label: "Email", type: "email" },
              { name: "phone", label: "Telephone", type: "tel" },
              { name: "dateOfBirth", label: "Date of birth", type: "date" },
              {
                name: "nationality",
                label: "Nationality",
                options: nationalities,
                searchable: true,
              },
              {
                name: "secondaryNationality",
                label: "Second nationality",
                options: nationalities,
                searchable: true,
              },
              { name: "placeOfBirth", label: "Place of birth" },
              {
                name: "birthCountry",
                label: "Country of birth",
                options: countryOptions(countries?.items),
                searchable: true,
              },
              {
                name: "residencyStatus",
                label: "Residency status",
                options: residencyStatusOptions(individual.residencyStatus),
              },
            ]}
          />
        </form>

        <NationalInsuranceNumber individual={individual} />
      </RecordColumn>
    </>
  )
}
